import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { CustomerStore } from 'iam/CustomerStore'
import { SearchableUser } from 'iam/models/SearchableUser'
import Workspace from 'iam/models/Workspace'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import ModalViewModel from 'infra/ModalViewModel'
import { Id, Nullable } from 'types/definitions'

export class AddCustomerToWorkspaceViewModel extends ModalViewModel {
  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Nullable<Workspace> = null

  /**
   * Validation rules for the add customer to workspace form.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
  })

  constructor(
    private _user: () => SearchableUser,
    private _refreshMemberships: () => Promise<void>,
    private newAlert: AlertFactory,
    private customerStore: CustomerStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create customers in.
   * Filters by CUSTOMER_CREATE permission and external_customers_config settings.
   */
  @computed
  get currentUserWorkspaces() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((mb) => {
        const hasPermission = mb.member.hasPermission('CUSTOMER_CREATE')
        const config = mb.workspace.config?.external_customers_config
        const isEnabled =
          config?.enabled && config?.allow_requesting_magic_links
        return hasPermission && isEnabled
      })
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces the currently selected user (i.e. the one being viewed)
   * is already a member of.
   */
  @computed
  get selectedUserWorkspaces() {
    return this.user.memberships.map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that the current user may create customers in
   * AND that the currently selected user is NOT already a member of.
   */
  @computed
  get availableWorkspaces() {
    return this.currentUserWorkspaces.filter(
      (cuws) =>
        !this.selectedUserWorkspaces.some((suws) => suws.id === cuws.id),
    )
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace: Nullable<Workspace>) {
    this.workspace = workspace
  }

  /**
   * Refreshes the user's workspace memberships.
   */
  async refreshMemberships() {
    await this._refreshMemberships()
  }

  /**
   * Reset the view model to its default state.
   */
  @action.bound
  reset() {
    this.setWorkspace(null)
    this.validation.reset()
  }

  /**
   * Add the currently selected customer to the selected workspace via magic link.
   */
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      await this.customerStore.create({
        workspace_id: Id.assert(this.workspace!.id),
        email: this.user.email,
        given_name: this.user.givenName,
        family_name: this.user.familyName,
        phone_number: this.user.phone || undefined,
      })

      const displayName = this.user.displayName
      const workspaceName = this.workspace?.name

      this.refreshMemberships()

      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Magic link invitation sent to ${displayName} for ${workspaceName}.`,
        )
        .autoDismiss(2500)
        .show()

      this.close()
    } catch (err) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(
          'Failed to send invitation.',
          `Could not send magic link to ${this.user.email}.`,
        )
        .autoDismiss(2500)
        .show()
    }
  })

  /**
   * Open the dialog.
   */
  @override
  show() {
    super.show()
  }

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    super.close()
    this.reset()
  }
}
